/**
 * Level Entity
 * Represents an academic level in the institution
 *
 * DB Table: Levels
 * - id (PK) -> bigint
 * - workspace_id (FK) -> bigint
 * - name -> varchar
 * - sort_order -> smallint
 */
export interface LevelProps {
  id: number;
  workspaceId: number;
  name: string;
  sortOrder: number;
  // Computed/aggregated fields (not in DB)
  subjectCount?: number;
  studentCount?: number;
}

export class Level {
  private readonly props: LevelProps;

  constructor(props: LevelProps) {
    this.props = props;
  }

  get id(): number {
    return this.props.id;
  }

  get workspaceId(): number {
    return this.props.workspaceId;
  }

  get name(): string {
    return this.props.name;
  }

  get sortOrder(): number {
    return this.props.sortOrder;
  }

  /**
   * Get the number of subjects in this level (aggregated)
   */
  get subjectCount(): number {
    return this.props.subjectCount ?? 0;
  }

  /**
   * Get the number of students in this level (aggregated)
   * @deprecated Use subjectCount instead - keeping for backwards compatibility
   */
  get subjects(): number {
    return this.subjectCount;
  }

  /**
   * Get the number of students enrolled in this level (aggregated)
   */
  get studentCount(): number {
    return this.props.studentCount ?? 0;
  }

  /**
   * @deprecated Use studentCount instead - keeping for backwards compatibility
   */
  get students(): number {
    return this.studentCount;
  }

  /**
   * Check if this level has any subjects
   */
  hasSubjects(): boolean {
    return this.subjectCount > 0;
  }

  /**
   * Check if this level has any enrolled students
   */
  hasStudents(): boolean {
    return this.studentCount > 0;
  }

  /**
   * Check if this level belongs to a specific workspace
   */
  belongsToWorkspace(workspaceId: number): boolean {
    return this.props.workspaceId === workspaceId;
  }

  /**
   * Compare sort order with another level
   */
  compareSortOrder(other: Level): number {
    return this.props.sortOrder - other.sortOrder;
  }

  toPlainObject(): LevelProps {
    return { ...this.props };
  }
}
